import React from 'react'
import { useApp } from '../../context/AppContext'
import { FormStarter, FormTemplate, Service, api } from '../../api/client'
import Panel, { PanelStatus } from './Panel'
import FormEditor from './FormEditor'
import { FormDraft, blankDraft, toDraft } from './formEditorDraft'
import { saveErrorMessage, showToast } from './toast'

type Editing = { template: FormTemplate | null; initial: FormDraft }

export default function FormsPanel() {
  const { dispatch } = useApp()
  const [status, setStatus] = React.useState<'loading' | 'error' | 'ready'>('loading')
  const [templates, setTemplates] = React.useState<FormTemplate[]>([])
  const [services, setServices] = React.useState<Service[]>([])
  const [starters, setStarters] = React.useState<FormStarter[] | null>(null)
  const [choosing, setChoosing] = React.useState(false)
  const [editing, setEditing] = React.useState<Editing | null>(null)
  const [error, setError] = React.useState('')

  const fetchAll = () => Promise.all([api.listFormTemplates(), api.listServices()])

  const load = React.useCallback(async () => {
    setStatus('loading')
    try {
      const [t, s] = await fetchAll()
      setTemplates(t)
      setServices(s)
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  const refresh = async () => {
    try {
      const [t, s] = await fetchAll()
      setTemplates(t)
      setServices(s)
    } catch {
      // Keep the list we have.
    }
  }

  const edit = async (summary: FormTemplate) => {
    setError('')
    try {
      const template = await api.getFormTemplate(summary.id)
      setEditing({ template, initial: toDraft(template) })
    } catch (err) {
      setError(saveErrorMessage(err))
    }
  }

  const startNew = async () => {
    setError('')
    setChoosing(true)
    if (starters) return
    try {
      setStarters(await api.listFormStarters())
    } catch {
      setStarters([]) // Blank still works.
    }
  }

  const begin = (starter: FormStarter | null) => {
    setChoosing(false)
    setEditing({
      template: null,
      initial: starter ? { ...blankDraft(), name: starter.name, fields: starter.fields } : blankDraft(),
    })
  }

  const saved = (message: string) => {
    setEditing(null)
    refresh()
    showToast(dispatch, message)
  }

  const active = templates.filter((t) => !t.archived_at)
  const serviceNames = (t: FormTemplate) =>
    t.service_ids.map((id) => services.find((s) => s.id === id)?.name).filter((n): n is string => !!n).join(', ')

  return (
    <>
      <Panel title="Consultation forms" intro="Health and consent forms your clients fill in before a treatment.">
        {status !== 'ready' ? (
          <PanelStatus status={status} onRetry={load} />
        ) : (
          <>
            {active.length === 0 ? (
              <p className="settings-status">No forms yet. Add your first one below.</p>
            ) : (
              <ul className="service-list">
                {active.map((t) => (
                  <li key={t.id} className="service-item form-editor__list-item">
                    <button type="button" className="service-item__main" onClick={() => edit(t)}>
                      <span className="service-item__name">{t.name}</span>
                      <span className="service-item__meta">
                        {t.current_version ? `Version ${t.current_version}` : 'Not published'}
                        {serviceNames(t) ? ` · ${serviceNames(t)}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {error && <p className="field-error" role="alert">{error}</p>}
            {choosing ? (
              <div className="chip-group" role="group" aria-label="Start from">
                {(starters ?? []).map((s) => (
                  <button key={s.key} type="button" className="chip" onClick={() => begin(s)}>{s.name}</button>
                ))}
                <button type="button" className="chip" onClick={() => begin(null)}>Blank form</button>
                <button type="button" className="chip chip--add" onClick={() => setChoosing(false)}>Cancel</button>
              </div>
            ) : (
              <button type="button" className="btn btn-primary btn-block" onClick={startNew}>New form</button>
            )}
          </>
        )}
      </Panel>
      {editing && (
        <FormEditor
          template={editing.template}
          initial={editing.initial}
          services={services}
          onClose={() => setEditing(null)}
          onSaved={saved}
        />
      )}
    </>
  )
}
